import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Loader2, Lock, Zap, FileText, Check,
  Eye, EyeOff, MessageCircle, Linkedin, CalendarClock, UserRound,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  api, TOKEN_KEY, WHATSAPP_LINK, LINKEDIN_LINK, CONTACT_LINK,
} from "../lib/api";
import { EASE, EASE_HOVER } from "../lib/motion";

const errMsg = (e, fallback) =>
  e?.response?.data?.detail && typeof e.response.data.detail === "string"
    ? e.response.data.detail
    : fallback;


const CodeModal = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", code: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCode, setShowCode] = useState(false);
  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid =
    form.name.trim() && form.company.trim() &&
    /\S+@\S+\.\S+/.test(form.email) && form.code.trim();

  const unlock = async (e) => {
    e?.preventDefault();
    if (!valid) { setError("Please fill in all fields, including your access code."); return; }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/access/unlock", {
        name: form.name.trim(), company: form.company.trim(),
        email: form.email.trim(), code: form.code.trim(),
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Access granted", { description: "Opening the full assessment..." });
      navigate("/assessment");
    } catch (err) {
      const m = errMsg(err, "Something went wrong. Please try again.");
      setError(m); toast.error(m);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[480px] rounded-2xl border-[rgba(3,33,126,0.14)] p-7"
        data-testid="code-modal"
      >
        <DialogHeader>
          <DialogTitle
            className="font-[var(--font-display)] text-[22px] tracking-tight text-[var(--evc-resolution)]"
          >
            Open the full assessment
          </DialogTitle>
          <DialogDescription className="text-[13.5px] leading-relaxed text-[var(--evc-fg2)]">
            Enter your details and the access code. An EVC expert sets you up with a code so they
            can walk you through the read.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={unlock} className="evc-modal-form" noValidate>
          <div className="evc-modal-grid">
            <input className="evc-input" placeholder="Full name" value={form.name}
              onChange={onField("name")} data-testid="code-modal-name" />
            <input className="evc-input" placeholder="Company" value={form.company}
              onChange={onField("company")} data-testid="code-modal-company" />
          </div>
          <input className="evc-input" type="email" placeholder="Work email" value={form.email}
            onChange={onField("email")} data-testid="code-modal-email" />
          <div style={{ position: "relative" }}>
            <input className="evc-input" type={showCode ? "text" : "password"} placeholder="Access code"
              value={form.code} onChange={onField("code")} style={{ paddingRight: 44 }}
              data-testid="code-modal-code" />
            <button type="button" onClick={() => setShowCode((s) => !s)} aria-label="Toggle code"
              style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--evc-fg3)", display: "inline-flex", padding: 6 }}>
              {showCode ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="evc-lite-nocode">
            No code yet? Ping us on{" "}
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="code-modal-nocode-wa">WhatsApp</a>{" "}
            or{" "}
            <a href={LINKEDIN_LINK} target="_blank" rel="noopener noreferrer" data-testid="code-modal-nocode-li">LinkedIn</a>{" "}
            - we'll set you up.
          </p>
          {error && <div className="evc-error" data-testid="code-modal-error">{error}</div>}
          <button type="submit" className="evc-btn evc-btn-dark" disabled={loading} data-testid="code-modal-submit">
            {loading ? <Loader2 size={16} className="evc-spin" /> : null}
            {loading ? "Opening..." : "Open full assessment"} {!loading && <ArrowRight size={16} />}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ContactModal = ({ open, onOpenChange }) => {
  const hasBooking = CONTACT_LINK && CONTACT_LINK !== "#";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[460px] rounded-2xl border-[rgba(3,33,126,0.14)] p-7"
        data-testid="contact-modal"
      >
        <DialogHeader>
          <DialogTitle
            className="font-[var(--font-display)] text-[22px] tracking-tight text-[var(--evc-resolution)]"
          >
            Talk to an EVC expert
          </DialogTitle>
          <DialogDescription className="text-[13.5px] leading-relaxed text-[var(--evc-fg2)]">
            Tell us where you are and we'll match you with a senior advisor who reads your results
            with you - turning the radar into a prioritised, board-ready plan.
          </DialogDescription>
        </DialogHeader>

        <div className="evc-contact-actions">
          <a className="evc-contact-btn" href={WHATSAPP_LINK} target="_blank"
            rel="noopener noreferrer" data-testid="contact-modal-whatsapp">
            <span className="ic"><MessageCircle size={18} /></span>
            <span className="lbl">Message us on WhatsApp<small>Fastest way to reach the team</small></span>
            <ArrowRight size={16} style={{ marginLeft: "auto", color: "var(--evc-fg3)" }} />
          </a>
          <a className="evc-contact-btn" href={LINKEDIN_LINK} target="_blank"
            rel="noopener noreferrer" data-testid="contact-modal-linkedin">
            <span className="ic"><Linkedin size={18} /></span>
            <span className="lbl">Connect on LinkedIn<small>Reach Marc directly</small></span>
            <ArrowRight size={16} style={{ marginLeft: "auto", color: "var(--evc-fg3)" }} />
          </a>
          <a className={`evc-contact-btn ${hasBooking ? "" : "disabled"}`}
            href={hasBooking ? CONTACT_LINK : undefined}
            target={hasBooking ? "_blank" : undefined}
            rel="noopener noreferrer"
            aria-disabled={!hasBooking}
            data-testid="contact-modal-booking">
            <span className="ic"><CalendarClock size={18} /></span>
            <span className="lbl">Book a call{hasBooking
              ? <small>Pick a slot that suits you</small>
              : <small>Scheduling link coming soon</small>}</span>
            <ArrowRight size={16} style={{ marginLeft: "auto", color: "var(--evc-fg3)" }} />
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ChoosePath = ({ onLight, onBack }) => {
  const reduce = useReducedMotion();
  const [codeOpen, setCodeOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
  };
  const item = {
    hidden: { opacity: 0, ...(reduce ? {} : { y: 30 }) },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
  };
  // hero card settles with a slight scale-down and rests gently raised
  const itemHero = {
    hidden: { opacity: 0, ...(reduce ? {} : { y: 30, scale: 1.02 }) },
    show: { opacity: 1, y: reduce ? 0 : -10, scale: 1, transition: { duration: 0.8, ease: EASE } },
  };
  const hoverLift = reduce ? {} : { y: -2, transition: { duration: 0.3, ease: EASE_HOVER } };

  return (
    <section className="evc-choosepath" data-testid="choose-path">
      <motion.div
        className="evc-pagehdr"
        initial={{ opacity: 0, ...(reduce ? {} : { y: 10 }) }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
      >
        <div className="evc-pagehdr-strip">
          <span className="evc-pagehdr-chip">Choose your path</span>
          <span className="evc-pagehdr-line" />
          <span className="evc-pagehdr-rt">From a fast read to a guided expert read</span>
        </div>
        <h2 className="evc-pagehdr-title">Pick the depth that fits your decision.</h2>
        <p className="evc-pagehdr-lede">
          Three ways in - a fast self-check, the full diagnostic, or that diagnostic read with you
          one-on-one by an EVC expert.
        </p>
      </motion.div>

      <motion.div
        className="evc-tier-grid"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
      >
        {/* Tier 1 - Quick check (free on-ramp) */}
        <motion.div className="evc-tier" variants={item} whileHover={hoverLift} data-testid="tier-quick">
          <span className="evc-tier-badge"><Zap size={12} /> Free</span>
          <div className="evc-tier-icon"><Zap size={20} /></div>
          <h3 className="evc-tier-name">Quick check</h3>
          <p className="evc-tier-price"><span className="amt">Free</span></p>
          <p className="evc-tier-sub">
            One question per dimension - a fast read on where your technology spend stands. See your
            shape across all eight dimensions in 5-10 minutes.
          </p>
          <p className="evc-tier-listlabel">What you get</p>
          <ul className="evc-tier-list">
            <li><Check size={15} /> A score across all 8 dimensions</li>
            <li><Check size={15} /> Your strongest and weakest dimension</li>
            <li><Check size={15} /> A taste of the full maturity assessment</li>
          </ul>
          <button className="evc-btn evc-tier-cta light-cta" onClick={onLight} data-testid="tier-quick-btn">
            Start the quick check <ArrowRight size={16} />
          </button>
        </motion.div>

        {/* Tier 2 - Full diagnostic (the single hero) */}
        <motion.div className="evc-tier featured" variants={itemHero} data-testid="tier-full">
          <span className="evc-tier-badge gated"><Lock size={11} /> Code required</span>
          <div className="evc-tier-icon"><FileText size={20} /></div>
          <h3 className="evc-tier-name">Full diagnostic</h3>
          <p className="evc-tier-price access"><span className="amt-text">By access code</span></p>
          <p className="evc-tier-pricesub">Invitation only</p>
          <p className="evc-tier-sub">
            The complete diagnostic - all 56 questions across eight dimensions, with a full radar
            profile, your maturity archetype and a detailed report on where you stand today, with
            clear recommendations.
          </p>
          <p className="evc-tier-listlabel">Everything in Quick check, plus</p>
          <ul className="evc-tier-list">
            <li><Check size={15} /> All 56 questions across 8 dimensions</li>
            <li><Check size={15} /> Full radar profile + maturity archetype</li>
            <li><Check size={15} /> A detailed report on your position, with recommendations</li>
          </ul>
          <button className="evc-btn evc-tier-cta" onClick={() => setCodeOpen(true)} data-testid="tier-full-btn">
            Enter your access code <ArrowRight size={16} />
          </button>
        </motion.div>

        {/* Tier 3 - Full + expert read (quiet anchor) */}
        <motion.div className="evc-tier" variants={item} whileHover={hoverLift} data-testid="tier-expert">
          <span className="evc-tier-guided">Guided</span>
          <div className="evc-tier-icons">
            <div className="evc-tier-icon"><FileText size={20} /></div>
            <span className="evc-tier-icon-plus">+</span>
            <div className="evc-tier-icon"><UserRound size={20} /></div>
          </div>
          <h3 className="evc-tier-name">Full diagnostic + expert read</h3>
          <p className="evc-tier-price">starting from <span className="amt">€2,000</span></p>
          <p className="evc-tier-sub">
            The full self-service assessment plus an expert reading - a senior EVC expert reads your
            results with you, turning the radar into a prioritised, board-ready action plan.
          </p>
          <div className="evc-tier-human">
            <span className="lbl">The human layer</span>
            <span className="txt">A senior EVC advisor reads your radar with you, one-on-one.</span>
          </div>
          <p className="evc-tier-listlabel">Everything in Full diagnostic, plus</p>
          <ul className="evc-tier-list">
            <li><Check size={15} /> A 1:1 expert read of your results</li>
            <li><Check size={15} /> A prioritised action plan</li>
            <li><Check size={15} /> Direct line to a senior EVC advisor</li>
            <li><Check size={15} /> Context tailored to your seat and sector</li>
          </ul>
          <button className="evc-btn evc-tier-cta light-cta" onClick={() => setContactOpen(true)} data-testid="tier-expert-btn">
            Talk to an expert <ArrowRight size={16} />
          </button>
        </motion.div>
      </motion.div>

      <div className="evc-lite-nav">
        <button className="evc-lite-ghost" onClick={onBack} data-testid="choose-path-back">
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      <CodeModal open={codeOpen} onOpenChange={setCodeOpen} />
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </section>
  );
};
